import { Plus } from "lucide-react";
import React from "react";

import { Action, getHeaderKey, TableSize, tableSizeConfigs } from "./dataTableUtils";

import { getThemeClass, getThemeComponentClasses } from "@/shared/lib/themeComponentClasses";
import {
  ActionsDropdown,
  Filters,
  PaginationControls,
  SearchInput,
  SortIcon,
} from "./DataTableParts";
import { debounce } from "./common";
import { useDataTable } from "./useDataTable";

interface IButton {
  name: string;
  onClick: Function;
}

interface ISecondaryButton {
  name: string;
  onClick: Function;
  variant?: "primary" | "secondary";
  disabled?: boolean;
}

interface TableFilter {
  name: string;
  valueKey: string;
  requests?: { url: string; reducer: any; method: "GET" | "POST" | "PUT" | "DELETE" }; // optional additional requests to make on filter change

  options: { value: string | number; label: string }[];
  placeholder?: string;
  width?: number | string;
}

interface DataTableProps {
  headers: React.ReactNode[];
  headerKeys?: string[];
  rows: any[];
  totalCount?: number;
  actions?: Action[];
  emptyText?: string;
  button?: IButton;
  secondaryButton?: ISecondaryButton;
  renderRow?: (row: any, rowIndex?: number) => React.ReactNode;
  url?: string;
  listReducer?: any;
  isPagination?: boolean;
  defaultSelectedRows?: any[];
  params?: Record<string, any>;
  tableSize?: TableSize;
  filters?: TableFilter[];
  showCheckbox?: boolean;
  onSelectionChange?: (selectedRows: any[]) => void;
  sortable?: boolean;
  nonSortableColumns?: string[];
  defaultSort?: {
    key: string;
    direction: "asc" | "desc";
  };
  pageSize?: number;
  refreshKey?: number;
  sidecardContent?: (row: any) => React.ReactNode;
  maxHeightClass?: string;
  staticHeight?: number;
  showSerialNumber?: boolean;
  searchPlaceholder?: string;
  searchWidth?: string | number;
  controlledFilters?: Record<string, string | number | null | undefined>;
  onFilterChange?: (name: string, value: string | number) => void;
  shouldFetch?: (requestBody: any) => boolean;
  isLoading?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
  onRowClick?: (row: any, rowIndex?: number) => void;
  showSearch?: boolean;
  syncUrl?: boolean;
  extraControls?: React.ReactNode;
}

export const DataTable: React.FC<DataTableProps> = ({
  button,
  secondaryButton,
  url,
  headers,
  headerKeys,
  rows,
  totalCount,
  actions = [],
  emptyText = "No data found",
  renderRow,
  listReducer,
  isPagination = true,
  defaultSelectedRows,
  params = {},
  tableSize = "default",
  filters = [],
  showCheckbox = false,
  onSelectionChange,
  sortable = false,
  nonSortableColumns = [],
  defaultSort,
  pageSize = 10,
  refreshKey,
  sidecardContent,
  maxHeightClass,
  staticHeight = 77,
  showSerialNumber = false,
  searchPlaceholder = "Search...",
  searchWidth = "40%",
  controlledFilters,
  onFilterChange,
  shouldFetch,
  isLoading = false,
  onLoadingChange,
  onRowClick,
  showSearch = true,
  syncUrl = true,
  extraControls,
}) => {
  const tableClasses = getThemeComponentClasses().table;
  const currentConfig = tableSizeConfigs[tableSize];
  const showActions = actions.length > 0;
  const [selectedRow, setSelectedRow] = React.useState<any | null>(null);
  const [currentPageSize, setCurrentPageSize] = React.useState(pageSize);
  const selectAllRef = React.useRef<HTMLInputElement>(null);
  console.log("listReducer", listReducer);

  React.useEffect(() => {
    setCurrentPageSize(pageSize);
  }, [pageSize]);

  const {
    isLoading: isRequestLoading,
    requestBody,
    page,
    paginatedRows,
    selectedRows,
    isAllSelected,
    isIndeterminate,
    pagination,
    handleSetPage,
    handleSearchChange,
    handleFilterChange,
    handleSelectRow,
    handleSelectAll,
    handleSort,
  } = useDataTable({
    rows,
    url,
    listReducer,
    isPagination,
    defaultSelectedRows,
    params,
    sortable,
    pageSize: currentPageSize,
    onSelectionChange,
    refreshKey,
    controlledFilters,
    onFilterChange,
    shouldFetch,
    syncUrl,
  });
  const isTableLoading = isLoading || isRequestLoading;

  React.useEffect(() => {
    onLoadingChange?.(isTableLoading);
  }, [isTableLoading, onLoadingChange]);

  function renderSidecard(row: any) {
    const isContentAvailable = paginatedRows?.some((r) => r?.id === row?.id) && sidecardContent;
    console.log("selectedRow", selectedRow, paginatedRows);
    if (isContentAvailable && selectedRow) return true;
  }

  const serialNumberOffset = isPagination ? (Math.max(page, 1) - 1) * currentPageSize : 0;
  const totalColumns =
    headers.length + (showCheckbox ? 1 : 0) + (showSerialNumber ? 1 : 0) + (showActions ? 1 : 0);

  React.useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const showTopControls =
    showSearch || filters?.length > 0 || Boolean(button?.name) || Boolean(secondaryButton?.name);

  return (
    <div>
      {/* Top Controls */}
      {showTopControls && (
        <div className="flex justify-between items-center  gap-2">
          <div className="flex gap-3 items-center flex-1">
            {showSearch && (
              <SearchInput
                query={requestBody.search}
                onChange={debounce(handleSearchChange, 1000)}
                placeholder={searchPlaceholder}
                width={searchWidth}
              />
            )}

            {filters?.length > 0 && (
              <Filters
                filters={filters}
                values={requestBody.filters || {}}
                onChange={handleFilterChange}
                requests={filters.flatMap((f) => f.requests || [])}
              />
            )}

            {extraControls}
          </div>

          {secondaryButton?.name && (
            <button
              disabled={secondaryButton.disabled}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${getThemeClass(tableClasses.accentBgColor, "bg-theme-primary")} ${getThemeClass(tableClasses.accentTextColor, "text-theme-text-inverse")} hover:bg-theme-primary-hover`}
              onClick={() => secondaryButton.onClick()}
            >
              <Plus className="w-4 h-4" />
              {secondaryButton.name}
            </button>
          )}
          {button?.name && (
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${getThemeClass(tableClasses.accentBgColor, "bg-theme-primary")} ${getThemeClass(tableClasses.accentTextColor, "text-theme-text-inverse")} hover:bg-theme-primary-hover`}
              onClick={() => button.onClick()}
            >
              <Plus className="w-4 h-4" />
              {button.name}
            </button>
          )}
        </div>
      )}

      <div className="flex  gap-2 w-full mt-2 ">
        {/* Table */}
        <div
          className={`
      transition-all duration-300
       ${renderSidecard(selectedRow) ? "w-[70%]" : "w-full"}
      ${currentConfig.containerClass}
    `}
        >
          <div
            className={`${maxHeightClass || currentConfig.maxHeight} overflow-y-auto thin-scrollbar`}
            style={{ maxHeight: `${staticHeight}vh` }}
          >
            <table className="min-w-full text-sm border-collapse">
              <thead className="bg-blue-50 sticky top-0 z-10">
                <tr>
                  {showSerialNumber && (
                    <th className={`${currentConfig.headerClass} w-14`}>SR No.</th>
                  )}
                  {showCheckbox && (
                    <th className={`${currentConfig.headerClass} w-10`}>
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 cursor-pointer accent-[#008060]"
                      />
                    </th>
                  )}

                  {headers.map((header, idx) => {
                    // Node headers carry their own markup, so they have no sort key.
                    const isPlainHeader = typeof header === "string" || typeof header === "number";
                    const headerKey =
                      headerKeys?.[idx] ||
                      (isPlainHeader ? getHeaderKey(header, idx) : String(idx));
                    const headerStr = isPlainHeader ? String(header) : "";
                    const isSortableColumn = sortable && !nonSortableColumns.includes(headerStr);
                    const isActive =
                      requestBody.ordering === headerKey ||
                      requestBody.ordering === `-${headerKey}`;
                    const direction =
                      requestBody.ordering === `-${headerKey}`
                        ? "desc"
                        : requestBody.ordering === headerKey
                          ? "asc"
                          : null;

                    return (
                      <th
                        key={idx}
                        className={`${currentConfig.headerClass} ${
                          isSortableColumn ? "cursor-pointer hover:bg-gray-100" : ""
                        }`}
                        onClick={() => isSortableColumn && handleSort(headerKey)}
                      >
                        <div className="flex items-center">
                          {header}
                          {isSortableColumn && <SortIcon active={isActive} direction={direction} />}
                        </div>
                      </th>
                    );
                  })}

                  {showActions && (
                    <th className={`${currentConfig.headerClass} rounded-tr-xl!`}>Actions</th>
                  )}
                </tr>
              </thead>

              <tbody>
                {paginatedRows?.length > 0 ? (
                  paginatedRows.map((row, rowIdx) => (
                    <tr
                      key={row?.id}
                      onClick={() => {
                        setSelectedRow(row);
                        onRowClick?.(row, rowIdx);
                      }}
                      className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer ${row.id === selectedRow?.id ? "bg-gray-200" : ""}`}
                    >
                      {showSerialNumber && (
                        <td className={`${currentConfig.cellClass} w-14 font-medium`}>
                          {serialNumberOffset + rowIdx + 1}
                        </td>
                      )}
                      {showCheckbox && (
                        <td className={`${currentConfig.cellClass} w-10`}>
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(String(row.id))}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => {
                              handleSelectRow(row.id);
                            }}
                            className="w-4 h-4 cursor-pointer accent-[#008060]"
                          />
                        </td>
                      )}

                      {renderRow?.(row, rowIdx)}

                      {showActions && (
                        <td className={currentConfig.cellClass}>
                          <ActionsDropdown actions={actions} row={row} rowIdx={rowIdx} />
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={totalColumns}
                      className="text-center py-12 text-muted-foreground font-semibold"
                    >
                      <p>{emptyText}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {renderSidecard(selectedRow) && (
          <div
            className="border rounded-xl border-gray-200 bg-white flex-1 overflow-y-auto thin-scrollbar"
            style={{ maxHeight: `${staticHeight}vh` }}
          >
            {sidecardContent(selectedRow)}
          </div>
        )}
      </div>

      {/* Pagination */}
      {isPagination && rows?.length > 0 && !isTableLoading && (
        <PaginationControls
          page={page}
          TotalCount={totalCount}
          selectedCount={selectedRows.length}
          count={pagination.count}
          onPageChange={handleSetPage}
          pageSize={currentPageSize}
          onPageSizeChange={setCurrentPageSize}
        />
      )}
    </div>
  );
};
